import { Contact, ContactChannel, ContactChannelType } from './types/Contact';
import { looksLikeChannelValue } from './contactNameUtils';

export const contactChannelTypeOptions: ContactChannelType[] = ['phone', 'whatsApp', 'instagram', 'telegram', 'messenger', 'tikTok', 'signal', 'other', 'email'];
export const manuallyManagedContactChannelTypeOptions: ContactChannelType[] = contactChannelTypeOptions;

export const isContactChannelManuallyEditable = (_type: ContactChannelType): boolean => true;

export const getContactChannelTypeLabel = (type: ContactChannelType): string => {
  switch (type) {
    case 'phone':
      return 'Phone';
    case 'whatsApp':
      return 'WhatsApp';
    case 'instagram':
      return 'Instagram';
    case 'telegram':
      return 'Telegram';
    case 'messenger':
      return 'Messenger';
    case 'tikTok':
      return 'TikTok';
    case 'signal':
      return 'Signal';
    case 'email':
      return 'Email';
    default:
      return 'Other';
  }
};

export const createEmptyContactChannel = (type: ContactChannelType = 'phone'): ContactChannel => ({
  type,
  label: '',
  value: '',
  isPrimary: true,
});

export const normalizeWhatsAppPhone = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/[\s()+-]/g, '');
  return /^\+?\d{7,15}$/.test(trimmed) || /^\d{7,15}$/.test(normalized)
    ? normalized.replace(/^\+/, '')
    : null;
};

export const sortContactChannels = (channels: ContactChannel[]): ContactChannel[] => {
  return [...channels].sort((left, right) => {
    if (left.isPrimary !== right.isPrimary) return left.isPrimary ? -1 : 1;
    return `${left.type}:${left.label}:${left.value}`.localeCompare(`${right.type}:${right.label}:${right.value}`);
  });
};

export const ensurePrimaryContactChannels = (channels: ContactChannel[]): ContactChannel[] => {
  const sorted = sortContactChannels(channels.filter(channel => channel.value.trim().length > 0 || channel.label.trim().length > 0));
  const seenPrimary = new Set<ContactChannelType>();

  return sorted.map(channel => {
    if (!seenPrimary.has(channel.type)) {
      seenPrimary.add(channel.type);
      return { ...channel, isPrimary: true };
    }

    return { ...channel, isPrimary: false };
  });
};

export const getPrimaryChannelByType = (channels: ContactChannel[], type: ContactChannelType): ContactChannel | null => {
  return sortContactChannels(channels).find(channel => channel.type === type) ?? null;
};

export const getPreferredChannel = (channels: ContactChannel[]): ContactChannel | null => {
  return getPrimaryChannelByType(channels, 'whatsApp')
    ?? getPrimaryChannelByType(channels, 'phone')
    ?? sortContactChannels(channels)[0]
    ?? null;
};

export const contactMatchesChannelTypes = (contact: Contact, selectedTypes: ContactChannelType[]): boolean => {
  if (selectedTypes.length === 0) {
    return true;
  }

  return contact.channels.some(channel => channelMatchesFilterTypes(channel, selectedTypes));
};

const channelMatchesFilterTypes = (channel: ContactChannel, selectedTypes: ContactChannelType[]): boolean => {
  if (selectedTypes.includes(channel.type)) {
    return true;
  }

  if (channel.type !== 'other') {
    return false;
  }

  const label = channel.label.trim().toLowerCase();
  if (selectedTypes.includes('instagram') && label === 'instagram') {
    return true;
  }

  if (selectedTypes.includes('messenger') && label === 'messenger') {
    return true;
  }

  if (selectedTypes.includes('tikTok') && label === 'tiktok') {
    return true;
  }

  if (selectedTypes.includes('other')
    && label.length > 0
    && !['instagram', 'messenger', 'tiktok', 'webchat', 'profile_pic', 'inbox', 'gender', 'timezone'].includes(label)) {
    return true;
  }

  return false;
};

export const getChannelLink = (channel: ContactChannel): string | null => {
  const storedLink = channel.link?.trim();
  if (storedLink) {
    return storedLink;
  }

  const trimmedValue = channel.value.trim();
  if (!trimmedValue) return null;

  const normalizedLabel = channel.label.trim().toLowerCase();

  if ((normalizedLabel === 'profile_pic' || normalizedLabel === 'inbox') && /^https?:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  if (channel.type === 'other' && normalizedLabel === 'instagram' && !/^\d+$/.test(trimmedValue)) {
    const username = trimmedValue.replace(/^@/, '');
    return username ? `https://instagram.com/${encodeURIComponent(username)}` : null;
  }

  switch (channel.type) {
    case 'phone':
      return `tel:${trimmedValue}`;
    case 'whatsApp': {
      const phone = normalizeWhatsAppPhone(trimmedValue);
      return phone ? `https://wa.me/${phone}` : null;
    }
    case 'instagram': {
      if (/^\d+$/.test(trimmedValue)) return null;
      const username = trimmedValue.replace(/^@/, '');
      return username ? `https://instagram.com/${encodeURIComponent(username)}` : null;
    }
    case 'telegram': {
      if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
      const username = trimmedValue.replace(/^@/, '').trim();
      return username ? `https://t.me/${encodeURIComponent(username)}` : null;
    }
    case 'messenger':
      return storedLink ?? null;
    default:
      return null;
  }
};

export const getChannelSummary = (channels: ContactChannel[]): string => {
  const sorted = sortContactChannels(channels);
  if (sorted.length === 0) return '-';

  return sorted
    .slice(0, 2)
    .map(channel => channel.label.trim().length > 0 ? `${channel.label}: ${channel.value}` : channel.value)
    .join(' · ');
};

export const MASK_CHARACTER = '•';

export const maskContactChannelValue = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return trimmed;
  }

  if (trimmed.length <= 2) {
    return MASK_CHARACTER.repeat(trimmed.length);
  }

  if (trimmed.length <= 4) {
    return `${trimmed[0]}${MASK_CHARACTER.repeat(trimmed.length - 1)}`;
  }

  const prefixLength = Math.min(3, trimmed.length - 2);
  const suffixLength = 2;
  return `${trimmed.slice(0, prefixLength)}${MASK_CHARACTER.repeat(trimmed.length - prefixLength - suffixLength)}${trimmed.slice(-suffixLength)}`;
};

export const displayContactChannelValue = (
  value: string,
  options?: { isMasked?: boolean; maskingEnabled?: boolean; revealed?: boolean },
): string => {
  if (!value) {
    return value;
  }

  if (options?.isMasked) {
    return value;
  }

  if (options?.maskingEnabled && !options.revealed) {
    return maskContactChannelValue(value);
  }

  return value;
};

const CHANNEL_TOKEN_TRAILING_PUNCTUATION = /[.,:;!?]+$/;

const maskChannelIdentityToken = (token: string): string => {
  if (!token.trim() || token.includes(MASK_CHARACTER)) {
    return token;
  }

  const trailing = token.match(CHANNEL_TOKEN_TRAILING_PUNCTUATION)?.[0] ?? '';
  const core = trailing ? token.slice(0, -trailing.length) : token;

  if (looksLikeChannelValue(core)) {
    return `${maskContactChannelValue(core)}${trailing}`;
  }

  if (looksLikeChannelValue(token)) {
    return maskContactChannelValue(token);
  }

  return token;
};

export const maskChannelIdentitiesInText = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes(MASK_CHARACTER)) {
    return trimmed ? trimmed : value;
  }

  if (looksLikeChannelValue(trimmed)) {
    return maskContactChannelValue(trimmed);
  }

  return trimmed
    .split(/(\s+)/)
    .map(maskChannelIdentityToken)
    .join('');
};

export const maybeMaskChannelIdentitiesInText = (
  value: string | undefined | null,
  enabled: boolean,
): string => {
  const text = value ?? '';
  return enabled ? maskChannelIdentitiesInText(text) : text;
};